import { useState } from "react";
import { listAuditLogs } from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type AuditLog = {
  id: string;
  orgId: string | null;
  userId: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string | null;
  createdAt: string;
};

export function AdminAuditPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ orgId: "", userId: "", action: "", resourceType: "" });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = (await listAuditLogs(token, {
        orgId: filters.orgId || undefined,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        resourceType: filters.resourceType || undefined,
        limit: 50,
      })) as AuditLog[];
      setLogs(res ?? []);
      setTotal(res?.length ?? 0);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Audit Center</h1>
          {total > 0 && (
            <span className="text-sm text-gray-400">{total.toLocaleString()} total entries</span>
          )}
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { key: "orgId", placeholder: "Filter by org ID" },
              { key: "userId", placeholder: "Filter by user ID" },
              { key: "action", placeholder: "Filter by action" },
              { key: "resourceType", placeholder: "Filter by resource type" },
            ].map(({ key, placeholder }) => (
              <input
                key={key}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
                placeholder={placeholder}
                value={filters[key as keyof typeof filters]}
                onChange={(e) => setFilters((p) => ({ ...p, [key]: e.target.value }))}
              />
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {logs.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <table className="w-full text-sm text-gray-300">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="pb-2">Time</th>
                  <th className="pb-2">Action</th>
                  <th className="pb-2">Resource</th>
                  <th className="pb-2">User</th>
                  <th className="pb-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-800 last:border-0">
                    <td className="py-2 text-xs text-gray-500">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 font-mono text-xs text-indigo-400">{log.action}</td>
                    <td className="py-2 text-xs">
                      <span className="text-gray-400">{log.resourceType}</span>
                      {log.resourceId && (
                        <span className="ml-1 text-gray-600">#{log.resourceId.slice(-8)}</span>
                      )}
                    </td>
                    <td className="py-2 text-xs text-gray-400">{log.userId ?? "system"}</td>
                    <td className="py-2 text-xs text-gray-500">{log.ipAddress ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {logs.length === 0 && !loading && (
          <div className="py-12 text-center text-gray-500">
            No audit logs found. Apply filters and search.
          </div>
        )}
      </div>
    </Layout>
  );
}
